import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TabGroup, TabList, Tab } from '@headlessui/react';
import { mockPatients, mockCampaigns } from '@/data/mock';
import {
  OPPORTUNITY_DEFS,
  OPPORTUNITY_ORDER,
  TIME_RANGE_LABEL,
  TIME_RANGE_ORDER,
  TODAY,
  type TimeRange,
  getSignalCounts,
  getSignals,
} from '@/lib/signals';
import type {
  Campaign,
  CampaignChannelType,
  OpportunityKind,
} from '@/lib/types';
import { OpportunityCard } from '@/components/signals/OpportunityCard';
import { CampaignDrawer } from '@/components/signals/CampaignDrawer';
import { CampaignDetailDrawer } from '@/components/signals/CampaignDetailDrawer';
import { CampaignsView } from '@/components/signals/CampaignsView';
import { OpportunityPatientsModal } from '@/components/signals/OpportunityPatientsModal';

type SignalsView = 'opportunities' | 'campaigns';

const TIME_RANGES = TIME_RANGE_ORDER;

function isTimeRange(v: string | null): v is TimeRange {
  return v === '3m' || v === 'year' || v === 'all';
}

function isView(v: string | null): v is SignalsView {
  return v === 'opportunities' || v === 'campaigns';
}

export function CareSignals() {
  const [searchParams, setSearchParams] = useSearchParams();

  const view: SignalsView = isView(searchParams.get('view'))
    ? (searchParams.get('view') as SignalsView)
    : 'opportunities';

  const range: TimeRange = isTimeRange(searchParams.get('range'))
    ? (searchParams.get('range') as TimeRange)
    : '3m';

  const [campaignKind, setCampaignKind] = useState<OpportunityKind | null>(null);
  const [viewKind, setViewKind] = useState<OpportunityKind | null>(null);
  const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);
  const [editingDetail, setEditingDetail] = useState(false);
  // Local in-memory store seeded from mock. Replace with API once available.
  const [campaigns, setCampaigns] = useState<Campaign[]>(mockCampaigns);

  const updateCampaign = (id: string, patch: Partial<Campaign>) => {
    setCampaigns((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    setDetailCampaign((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
  };

  const removeCampaign = (id: string) => {
    setCampaigns((prev) => prev.filter((c) => c.id !== id));
    setDetailCampaign((prev) => (prev && prev.id === id ? null : prev));
  };

  const handleSelectCampaign = (c: Campaign) => {
    setEditingDetail(false);
    setDetailCampaign(c);
  };

  const handleEditCampaign = (c: Campaign) => {
    setEditingDetail(true);
    setDetailCampaign(c);
  };

  const handlePauseCampaign = (c: Campaign) => {
    updateCampaign(c.id, { status: 'paused' });
  };

  const handleResumeCampaign = (c: Campaign) => {
    updateCampaign(c.id, { status: 'active' });
  };

  const handleDuplicateCampaign = (c: Campaign) => {
    const today = TODAY.toISOString().slice(0, 10);
    const copy: Campaign = {
      ...c,
      id: `cmp-${Date.now()}`,
      status: 'draft',
      createdAt: today,
      sentAt: undefined,
      completedAt: undefined,
      metrics: { delivered: 0, opened: 0, responded: 0, scheduled: 0 },
    };
    setCampaigns((prev) => [copy, ...prev]);
    setDetailCampaign(null);
  };

  const handleDeleteCampaign = (c: Campaign) => {
    if (typeof window !== 'undefined') {
      const ok = window.confirm(`¿Eliminar la campaña "${OPPORTUNITY_DEFS[c.kind].title}"?`);
      if (!ok) return;
    }
    removeCampaign(c.id);
  };

  const setView = (v: SignalsView) => {
    const params = new URLSearchParams(searchParams);
    if (v === 'opportunities') params.delete('view');
    else params.set('view', v);
    setSearchParams(params, { replace: true });
  };

  const setRange = (r: TimeRange) => {
    const params = new URLSearchParams(searchParams);
    if (r === '3m') params.delete('range');
    else params.set('range', r);
    setSearchParams(params, { replace: true });
  };

  const counts = useMemo(
    () => getSignalCounts(mockPatients, range),
    [range]
  );

  const totalOpportunities = useMemo(
    () => OPPORTUNITY_ORDER.reduce((acc, k) => acc + counts[k], 0),
    [counts]
  );

  const campaignDef = campaignKind ? OPPORTUNITY_DEFS[campaignKind] : null;
  const campaignPatients = useMemo(
    () =>
      campaignKind
        ? getSignals(mockPatients, range, campaignKind).map((s) => s.patient)
        : [],
    [campaignKind, range]
  );

  const viewDef = viewKind ? OPPORTUNITY_DEFS[viewKind] : null;
  const viewPatients = useMemo(
    () =>
      viewKind
        ? getSignals(mockPatients, range, viewKind).map((s) => s.patient)
        : [],
    [viewKind, range]
  );

  const activeCount = campaigns.filter((c) => c.status === 'active').length;

  const handleLaunchCampaign = (channel: CampaignChannelType) => {
    if (!campaignKind) return;
    const today = TODAY.toISOString().slice(0, 10);
    const newCampaign: Campaign = {
      id: `cmp-${Date.now()}`,
      kind: campaignKind,
      channel,
      status: 'active',
      recipientCount: campaignPatients.length,
      createdAt: today,
      sentAt: today,
      metrics: { delivered: campaignPatients.length, opened: 0, responded: 0, scheduled: 0 },
    };
    setCampaigns((prev) => [newCampaign, ...prev]);
    setCampaignKind(null);
    setView('campaigns');
  };

  const viewTabClass = ({ selected }: { selected: boolean }) =>
    [
      'font-inherit text-[13px] py-2 px-1 mr-3 -mb-px border-b-2 cursor-pointer outline-none flex items-center gap-1.5 transition-colors hover:text-primary focus-visible:text-primary focus-visible:border-b-primary',
      selected
        ? 'text-primary font-semibold border-b-primary'
        : 'text-tertiary font-medium border-b-transparent',
    ].join(' ');

  return (
    <main>
      <div className="pane-content appear">
        <header className="mb-6">
          <p className="pane-eyebrow">
            Care Signals · {totalOpportunities} oportunidades · {activeCount} campaña
            {activeCount === 1 ? '' : 's'} activa{activeCount === 1 ? '' : 's'}
          </p>
          <h1 className="pane-title">
            {view === 'campaigns' ? 'Campañas' : 'Oportunidades clínicas'}
          </h1>
          <p className="pane-summary">
            {view === 'campaigns'
              ? 'Estado y resultados de las campañas hacia tus pacientes.'
              : 'Grupos de pacientes que necesitan seguimiento, prevención o nueva evaluación. Selecciona una tarjeta para ver los pacientes o crear una campaña.'}
          </p>
        </header>

        <TabGroup
          selectedIndex={view === 'opportunities' ? 0 : 1}
          onChange={(i) => setView(i === 0 ? 'opportunities' : 'campaigns')}
        >
          <TabList className="flex gap-1 border-b border-line mb-[18px]">
            <Tab className={viewTabClass}>
              Oportunidades
              <span className="text-[11px] font-semibold text-muted bg-soft px-1.5 py-px rounded-full tnum data-selected:bg-subtle data-selected:text-secondary">
                {totalOpportunities}
              </span>
            </Tab>
            <Tab className={viewTabClass}>
              Campañas
              <span className="text-[11px] font-semibold text-muted bg-soft px-1.5 py-px rounded-full tnum data-selected:bg-subtle data-selected:text-secondary">
                {campaigns.length}
              </span>
            </Tab>
          </TabList>
        </TabGroup>

        {view === 'opportunities' ? (
          <>
            <div className="flex items-center gap-2.5 mb-4 flex-wrap">
              <div className="flex gap-1.5 flex-wrap">
                {TIME_RANGES.map((r) => (
                  <RangeChip
                    key={r}
                    active={range === r}
                    label={TIME_RANGE_LABEL[r]}
                    onClick={() => setRange(r)}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 max-[880px]:grid-cols-2 max-[760px]:grid-cols-1">
              {OPPORTUNITY_ORDER.map((k) => (
                <OpportunityCard
                  key={k}
                  def={OPPORTUNITY_DEFS[k]}
                  count={counts[k]}
                  active={false}
                  onSelect={() => setViewKind(k)}
                  onCampaign={() => setCampaignKind(k)}
                />
              ))}
            </div>
          </>
        ) : (
          <CampaignsView
            campaigns={campaigns}
            onSelect={handleSelectCampaign}
            onPause={handlePauseCampaign}
            onResume={handleResumeCampaign}
            onEdit={handleEditCampaign}
            onDuplicate={handleDuplicateCampaign}
            onDelete={handleDeleteCampaign}
          />
        )}
      </div>

      <CampaignDrawer
        def={campaignDef}
        patients={campaignPatients}
        onClose={() => setCampaignKind(null)}
        onLaunch={handleLaunchCampaign}
      />

      <CampaignDetailDrawer
        campaign={detailCampaign}
        editing={editingDetail}
        onClose={() => setDetailCampaign(null)}
        onPause={handlePauseCampaign}
        onResume={handleResumeCampaign}
        onDuplicate={handleDuplicateCampaign}
        onDelete={handleDeleteCampaign}
      />

      <OpportunityPatientsModal
        def={viewDef}
        patients={viewPatients}
        onClose={() => setViewKind(null)}
      />
    </main>
  );
}

interface RangeChipProps {
  active: boolean;
  label: string;
  onClick: () => void;
}

function RangeChip({ active, label, onClick }: RangeChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium select-none transition-all border ${
        active
          ? 'bg-primary text-surface border-primary shadow-pressed'
          : 'bg-soft text-secondary border-transparent shadow-pill hover:bg-subtle hover:border-line-default'
      }`}
    >
      {label}
    </button>
  );
}
